'use client';

import { useCallback } from 'react';
import { saveResultsPerPagePreference } from '../../utils/resultsPerPageStorage';
import {
  useSearchControllers,
  useSearchEngine,
} from '../headless/SearchDefinitionContext';
import { registerNumberOfResultsAsDefault } from '../headless/pagination';

/**
 * Page-size state + selection for the SSR arm, shared by the desktop control
 * and the mobile drawer so a choice made on either surface hits the same
 * engine, keeps `numberOfResults` out of the URL, and persists identically.
 * Re-selecting the current size only persists it: registering a custom URL
 * size as the default would drop it from the URL, and the save ignores custom
 * sizes, but a standard size re-selected over a URL override is a real choice.
 *
 * `state`, `methods` and `engine` are exposed for the desktop control's
 * saved-preference reconciliation, which is deliberately owned by that single
 * mount rather than re-run by every surface that offers a page size.
 */
export const useSearchV2ResultsPerPage = () => {
  const controllers = useSearchControllers();
  const { state, methods } = controllers.useResultsPerPage();
  const engine = useSearchEngine();

  const selectResultsPerPage = useCallback(
    (numberOfResults: number) => {
      saveResultsPerPagePreference(numberOfResults);
      if (numberOfResults === state.numberOfResults) return;
      methods?.set(numberOfResults);
      registerNumberOfResultsAsDefault(engine, numberOfResults);
    },
    [methods, engine, state.numberOfResults]
  );

  return { state, methods, engine, selectResultsPerPage };
};
